import { resolveComponent as _resolveComponent, createVNode as _createVNode, withKeys as _withKeys, withCtx as _withCtx, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "px-6 md:px-8 pt-6 pb-20" }
const _hoisted_2 = { class: "max-w-[1180px]" }
const _hoisted_3 = { class: "mb-6 flex gap-3 items-center text-gray-600" }
const _hoisted_4 = { class: "flex items-center justify-between" }
const _hoisted_5 = { class: "flex items-center" }
const _hoisted_6 = {
  key: 0,
  class: "flex items-center justify-start sm:justify-end gap-2 mb-4 flex-wrap"
}
const _hoisted_7 = {
  key: 1,
  class: "overflow-x-auto p-2 -m-2"
}

import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/20/solid'
import _ from 'lodash'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import ProductionDayCalendar from '@/components/activity/ProductionDayCalendar.vue'
import AgentSetupBanner from '@/components/ai/AgentSetupBanner.vue'
import SetupBanner from '@/components/setup/SetupBanner.vue'
import SetupEmptyState from '@/components/setup/SetupEmptyState.vue'
import { useSetupState } from '@/composables/setupState'
import { useToast } from '@/composables/useToast'
import { localFilterToServer, newFilterOptions } from '@/helpers/filters'
import { isPlacedOrder } from '@/helpers/orderPayment'
import { toastPlanLimit } from '@/helpers/planLimits'
import privilegeService from '@/services/privilege.service'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType } from '@/store/mappers'


export default {
  __name: 'OrdersNeue',
  setup(__props) {

const router = useRouter()
const store = useStore()
const { visible: setupVisible } = useSetupState()

const loading = ref(true)
// const bottles = ref([])
const orderModalOpen = ref(false)
const pinModalOpen = ref(false)
const pin = ref('')
const flash = ref(false)

const current = ref(new Date())

const user = computed(() => mapRelationship({ type: 'user', id: store.getters.loggedInUserId }))
const hasPin = computed(() => user.value?.attributes.hide_financial_data)
const hideNumbers = computed(() => store.state.hideNumbers)

const audiences = computed(() =>
  mapType('audience').reduce((acc, audience) => {
    if (audience.attributes.date) {
      acc[audience.attributes.date] = audience
    }
    return acc
  }, {})
)

const audience = day => audiences.value?.[day]
const audienceDetail = day => mapRelationship(audience(day)?.relationships.audience_detail.data)?.attributes
const audienceSubscribers = day =>
  mapRelationship(audience(day)?.relationships.audience_activity_detail.data)?.attributes.number_of_cart_subscriptions

const dropWarning = day => {
  const drops = mapRelationships(audience(day)?.relationships.bottle_cycles.data)
  return audienceSubscribers(day) > 0 && (!drops.length || drops.some(x => !x.attributes.open_for_ordering))
}

// The calendar owns the visible range (7 or 14 days, reactive to the viewport);
// this page maps and fetches whatever it reports.
const visibleDates = ref([])

// Maps each audience in view onto the cell the shared calendar renders.
const calendarDays = computed(() =>
  visibleDates.value.map(day => {
    const detail = audienceDetail(day)
    return {
      date: day,
      total: detail?.sum_of_paid_orders.format ?? null,
      orders: detail?.number_of_paid_orders ?? 0,
      pickups: detail?.number_of_uber_pickups ?? 0,
      subscribers: audienceSubscribers(day) ?? 0,
      warning: !!dropWarning(day),
      locations: null,
      clickable: !!(canReadLists.value && audience(day))
    }
  })
)

const getAudiences = (from, to) => {
  api.call(
    'GET',
    `activity/fulfillment_slot_audiences?included=audience_detail,audience_activity_detail,bottle_cycles&date_from=${from}&date_to=${to}`
  )
}

const changeRange = ({ from, to, dates }) => {
  visibleDates.value = dates
  getAudiences(from, to)
}

const openAudience = day => {
  const match = audiences.value?.[day]
  if (canReadLists.value && match) router.push('/lists/' + match.id)
}

// BOT-1680: an order placed on an invoice belongs here before the invoice is paid.
const bottles = computed(() =>
  mapType('bottle')
    .filter(x => isPlacedOrder(x.attributes))
    .sort((a, b) => b.relationships.bottle_payment.data?.id - a.relationships.bottle_payment.data?.id)
)

// An empty NeueTablist still paints its ring and shadow, which reads as a rule
// hanging above the setup card, and filters over nothing are just noise.
const showingSetupEmptyState = computed(() => setupVisible.value && !bottles.value.length)

const canCreateOrder = computed(() => {
  return privilegeService.canAccessResourceAction('orders', null, 'create')
})

const canReadLists = computed(() => {
  return privilegeService.canAccessResourceAction('customers', null, 'read')
})

onMounted(() => {
  // BOT-1745: one request shaped for this list (the newest 25 placed orders
  // with the fields the rows read), instead of the general activity endpoint.
  api.call('GET', 'orders').then(() => {
    loading.value = false
  })
})

api.call('GET', 'audiences?included=none')
const allCustomers = computed(() => mapType('audience')?.find(x => x.attributes.slug === 'bottle::all-customers'))

const { showToast } = useToast()

const makeAudienceWithFilter = async filter => {
  const response = await api
    .call('POST', 'audiences', {
      audience: {
        is_root: false,
        audience_name: `Filtered list (created ${new Intl.DateTimeFormat([], {
          dateStyle: 'short',
          timeStyle: 'short'
        }).format(new Date())})`,
        root_audience_id: allCustomers.value.id,
        filter_groups_attributes: [{ filters_attributes: [localFilterToServer(filter)] }]
      }
    })
    .catch(toastPlanLimit(showToast))
  if (!response) return
  await router.push('/lists/' + response.data.data.id)
}

const validatePin = () => {
  api.call('POST', 'profile/validate?pin=' + pin.value, {}, { save: false }).then(data => {
    if (data.data.valid) {
      store.commit('SET_HIDE_NUMBERS', false)
      pinModalOpen.value = false
      pin.value = ''
    } else {
      flash.value = true
      setTimeout(() => {
        flash.value = false
      }, 200)
    }
  })
}

return (_ctx, _cache) => {
  const _component_SecondaryTop = _resolveComponent("SecondaryTop")
  const _component_NeueOrderModal = _resolveComponent("NeueOrderModal")
  const _component_InputField = _resolveComponent("InputField")
  const _component_FormElement = _resolveComponent("FormElement")
  const _component_FormSection = _resolveComponent("FormSection")
  const _component_NeueButton = _resolveComponent("NeueButton")
  const _component_TheModal = _resolveComponent("TheModal")
  const _component_DescriptionHeader = _resolveComponent("DescriptionHeader")
  const _component_BasicSpinner = _resolveComponent("BasicSpinner")
  const _component_NewFilterDropdown = _resolveComponent("NewFilterDropdown")
  const _component_BottleRow = _resolveComponent("BottleRow")
  const _component_TablistRow = _resolveComponent("TablistRow")
  const _component_NeueTablist = _resolveComponent("NeueTablist")
  const _component_EmptyState = _resolveComponent("EmptyState")
  const _component_NeueWell = _resolveComponent("NeueWell")
  const _component_NeueLoading = _resolveComponent("NeueLoading")
  const _component_SecondaryNav = _resolveComponent("SecondaryNav")

  return (_openBlock(), _createBlock(_component_SecondaryNav, null, {
    top: _withCtx(() => [
      _createVNode(_component_SecondaryTop, { text: "Orders" })
    ]),
    main: _withCtx(() => [
      _createVNode(_component_NeueOrderModal, {
        "is-open": orderModalOpen.value,
        onCloseModal: _cache[0] || (_cache[0] = $event => (orderModalOpen.value = false))
      }, null, 8, ["is-open"]),
      _createVNode(_component_TheModal, {
        "is-open": pinModalOpen.value,
        "modal-title": "Enter PIN to unblur",
        onCloseModal: _cache[3] || (_cache[3] = $event => (pinModalOpen.value = false))
      }, {
        main: _withCtx(() => [
          _createVNode(_component_FormSection, null, {
            default: _withCtx(() => [
              _createVNode(_component_FormElement, null, {
                default: _withCtx(() => [
                  _createVNode(_component_InputField, {
                    modelValue: pin.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((pin).value = $event)),
                    name: "PIN",
                    onKeyup: _withKeys(validatePin, ["enter"])
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        footer: _withCtx(() => [
          _createVNode(_component_NeueButton, {
            type: "secondary",
            onClick: _cache[2] || (_cache[2] = $event => (pinModalOpen.value = false))
          }, {
            default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
              _createTextVNode("Cancel", -1)
            ]))]),
            _: 1
          }),
          _createVNode(_component_NeueButton, {
            type: flash.value ? 'red' : 'primary',
            onClick: validatePin
          }, {
            default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
              _createTextVNode("Unblur", -1)
            ]))]),
            _: 1
          }, 8, ["type"])
        ]),
        _: 1
      }, 8, ["is-open"]),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(SetupBanner, { class: "mb-6" }),
          _createVNode(AgentSetupBanner, { class: "mb-6" }),
          _createElementVNode("div", _hoisted_3, [
            _cache[13] || (_cache[13] = _createElementVNode("h3", { class: "text-2xl font-bold tracking-tight" }, "Orders grouped by production date", -1)),
            (hasPin.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  (hideNumbers.value)
                    ? (_openBlock(), _createBlock(_unref(EyeSlashIcon), {
                        key: 0,
                        class: "h-5 w-5 cursor-pointer",
                        onClick: _cache[4] || (_cache[4] = $event => (pinModalOpen.value = true))
                      }))
                    : (_openBlock(), _createBlock(_unref(EyeIcon), {
                        key: 1,
                        class: "h-5 w-5 cursor-pointer",
                        onClick: _cache[5] || (_cache[5] = $event => (_unref(store).commit('SET_HIDE_NUMBERS', true)))
                      }))
                ], 64))
              : _createCommentVNode("", true)
          ]),
          _createVNode(ProductionDayCalendar, {
            days: calendarDays.value,
            current: current.value,
            "hide-numbers": hideNumbers.value,
            "onUpdate:current": _cache[6] || (_cache[6] = $event => (current.value = $event)),
            "onUpdate:range": changeRange,
            onSelect: openAudience
          }, null, 8, ["days", "current", "hide-numbers"]),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(_component_DescriptionHeader, { name: "All orders" }),
              (loading.value && bottles.value.length)
                ? (_openBlock(), _createBlock(_component_BasicSpinner, {
                    key: 0,
                    color: "gray-900",
                    class: "mt-3 ml-3"
                  }))
                : _createCommentVNode("", true)
            ]),
            (canCreateOrder.value)
              ? (_openBlock(), _createBlock(_component_NeueButton, {
                  key: 0,
                  onClick: _cache[7] || (_cache[7] = $event => (orderModalOpen.value = true))
                }, {
                  default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                    _createTextVNode("+ Order", -1)
                  ]))]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          (canReadLists.value && !showingSetupEmptyState.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createVNode(_component_NewFilterDropdown, {
                  direction: "left",
                  name: "Cart & payment filters",
                  filters: _unref(newFilterOptions).cart,
                  onAddFilter: _cache[8] || (_cache[8] = type => makeAudienceWithFilter({ type }))
                }, null, 8, ["filters"]),
                _createVNode(_component_NewFilterDropdown, {
                  direction: "left",
                  name: "Fulfillment filters",
                  filters: _unref(newFilterOptions).fulfillment,
                  onAddFilter: _cache[9] || (_cache[9] = type => makeAudienceWithFilter({ type }))
                }, null, 8, ["filters"]),
                _createVNode(_component_NewFilterDropdown, {
                  direction: "left",
                  name: "Contact filters",
                  filters: _unref(newFilterOptions).contact,
                  onAddFilter: _cache[10] || (_cache[10] = type => makeAudienceWithFilter({ type }))
                }, null, 8, ["filters"])
              ]))
            : _createCommentVNode("", true),
          (!showingSetupEmptyState.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createVNode(_component_NeueTablist, null, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bottles.value, (bottle) => {
                      return (_openBlock(), _createBlock(_component_TablistRow, null, {
                        default: _withCtx(() => [
                          (_openBlock(), _createBlock(_component_BottleRow, {
                            key: bottle.id,
                            bottle: bottle
                          }, null, 8, ["bottle"]))
                        ]),
                        _: 2
                      }, 1024))
                    }), 256))
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          _createVNode(_component_NeueLoading, {
            loading: loading.value && !bottles.value.length
          }, {
            default: _withCtx(() => [
              (!bottles.value.length)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    (_unref(setupVisible))
                      ? (_openBlock(), _createBlock(SetupEmptyState, { key: 0 }))
                      : (_openBlock(), _createBlock(_component_NeueWell, {
                          key: 1,
                          class: "text-center"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_EmptyState, {
                              plural: "recent orders",
                              description: ""
                            })
                          ]),
                          _: 1
                        }))
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["loading"])
        ])
      ])
    ]),
    _: 1
  }))
}
}

}